/**
 * Helper class for serializing and deserializing cache keys.
 *
 * Converts structured CacheKey objects to deterministic string representations
 * and handles pattern generation for bulk operations.
 *
 * Key format: namespace|id|context_key:value|context_key2:value2
 * Pattern format: namespace|id|* (for partial keys)
 */
export class CacheKeySerializationHelper {
    /**
     * Serializes a TypeCacheKey or string to a deterministic string representation.
     *
     * @param {string | TypeCacheKey} key - The key to serialize
     * @returns {string} Serialized key string
     *
     * @example
     * serialize('simple-key') // 'simple-key'
     * serialize({ namespace: 'license', id: '123' }) // 'license|123'
     * serialize({ namespace: 'license', id: '123', context: { action: 'activation' } }) // 'license|123|action:activation'
     */
    static serialize(key) {
        if (typeof key === 'string') {
            return key;
        }
        const parts = [key.namespace, key.id];
        if (key.context && Object.keys(key.context).length > 0) {
            // Sort context keys for deterministic output
            const sortedContextKeys = Object.keys(key.context).sort();
            for (const contextKey of sortedContextKeys) {
                const value = key.context[contextKey];
                if (value !== undefined && value !== null) {
                    // Escape special characters in context values
                    const escapedValue = this.escapeValue(value);
                    parts.push(`${contextKey}:${escapedValue}`);
                }
            }
        }
        return parts.join('|');
    }
    /**
     * Serializes a TypePartialCacheKey to a deterministic string representation.
     *
     * @param {TypePartialCacheKey} key - The partial key to serialize
     * @returns {string} Serialized key string
     *
     * @example
     * serializePartial({ namespace: 'license' }) // 'license'
     * serializePartial({ namespace: 'license', id: '123' }) // 'license|123'
     * serializePartial({ namespace: 'license', id: '123', context: { action: 'activation' } }) // 'license|123|action:activation'
     */
    static serializePartial(key) {
        const parts = [key.namespace];
        if (key.id) {
            parts.push(key.id);
        }
        if (key.context && Object.keys(key.context).length > 0) {
            // Sort context keys for deterministic output
            const sortedContextKeys = Object.keys(key.context).sort();
            for (const contextKey of sortedContextKeys) {
                const value = key.context[contextKey];
                if (value !== undefined && value !== null) {
                    // Escape special characters in context values
                    const escapedValue = this.escapeValue(value);
                    parts.push(`${contextKey}:${escapedValue}`);
                }
            }
        }
        return parts.join('|');
    }
    /**
     * Generates a pattern string for partial key matching.
     *
     * @param {TypePartialCacheKey} partialKey - Partial key for pattern generation
     * @returns {string} Pattern string with wildcards
     *
     * @example
     * generatePattern({ namespace: 'license', id: '123' }) // 'license|123|*'
     * generatePattern({ namespace: 'license' }) // 'license|*'
     * generatePattern({ namespace: 'license', context: { action: 'activation' } }) // 'license|*|action:activation|*'
     */
    static generatePattern(partialKey) {
        const parts = [partialKey.namespace];
        // Add ID if specified
        if (partialKey.id) {
            parts.push(partialKey.id);
        }
        else {
            // If no ID specified, this becomes a namespace-wide pattern
            parts.push('*');
            return parts.join('|');
        }
        // Add context if specified
        if (partialKey.context && Object.keys(partialKey.context).length > 0) {
            const sortedContextKeys = Object.keys(partialKey.context).sort();
            for (const contextKey of sortedContextKeys) {
                const value = partialKey.context[contextKey];
                if (value !== undefined && value !== null) {
                    const escapedValue = this.escapeValue(value);
                    parts.push(`${contextKey}:${escapedValue}`);
                }
            }
        }
        // Add wildcard for pattern matching
        parts.push('*');
        return parts.join('|');
    }
    /**
     * Checks if a given key is a pattern (contains wildcards).
     *
     * @param {string} key - The key to check
     * @returns {boolean} True if the key contains wildcards
     */
    static isPattern(key) {
        return key.includes('*');
    }
    /**
     * Converts a pattern to a regular expression for matching.
     *
     * @param {string} pattern - Pattern with wildcards
     * @returns {RegExp} Regular expression for pattern matching
     */
    static patternToRegex(pattern) {
        // Escape special regex characters except *
        const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
        // Convert * to .* (match any characters)
        const regexPattern = escaped.replace(/\*/g, '.*');
        // Return regex that matches the entire string
        return new RegExp(`^${regexPattern}$`);
    }
    /**
     * Escapes special characters in cache key values.
     *
     * @private
     * @param {string} value - Value to escape
     * @returns {string} Escaped value
     */
    static escapeValue(value) {
        // Escape pipe characters and wildcards that could interfere with serialization
        return value
            .replace(/\\/g, '\\\\') // Escape backslashes first
            .replace(/\|/g, '\\|') // Escape pipe separators
            .replace(/\*/g, '\\*'); // Escape wildcards
    }
}
